import axios from 'axios';

// Empty string in production = relative to the current origin (client and API
// are served from the same host/port). Set via REACT_APP_API_URL at build time
// (see client/.env.development for the local dev override, http://localhost:5000).
export const API_ORIGIN = process.env.REACT_APP_API_URL || '';
const API_BASE = `${API_ORIGIN}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Attach the JWT to every request.
api.interceptors.request.use(config => {
  try {
    const token = localStorage.getItem('manara_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  } catch {}
  return config;
});

// On 401 (missing/expired token) clear the session and return to Login.
// Public link pages (pay/sign/register) are left alone.
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      const path = window.location.pathname;
      const isPublic = path.startsWith('/pay/') || path.startsWith('/sign/') || path.startsWith('/register/');
      if (!isPublic) {
        localStorage.removeItem('manara_token');
        localStorage.removeItem('manara_user');
        localStorage.removeItem('manara_permissions');
        if (path !== '/') window.location.href = '/';
        else window.location.reload();
      }
    }
    return Promise.reject(err);
  }
);

// Students
export const getStudents = (params?: any) => api.get('/students', { params });
export const getStudent = (id: string) => api.get(`/students/${id}`);
export const createStudent = (data: any) => api.post('/students', data);
export const updateStudent = (id: string, data: any) => api.put(`/students/${id}`, data);
export const deleteStudent = (id: string) => api.delete(`/students/${id}`);

// Student Documents
export const getStudentDocuments = (studentId: string) => api.get(`/students/${studentId}/documents`);
export const uploadStudentDocument = (studentId: string, formData: FormData) =>
  axios.post(`${API_BASE}/students/${studentId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
export const deleteStudentDocument = (studentId: string, docId: string) =>
  api.delete(`/students/${studentId}/documents/${docId}`);

// Courses
export const getCourses = (params?: any) => api.get('/courses', { params });
export const getCourse = (id: string) => api.get(`/courses/${id}`);
export const createCourse = (data: any) => api.post('/courses', data);
export const updateCourse = (id: string, data: any) => api.put(`/courses/${id}`, data);
export const deleteCourse = (id: string) => api.delete(`/courses/${id}`);
export const checkCourseConflicts = (data: any) => api.post('/courses/check-conflicts', data);
export const enrollStudentInCourse = (courseId: string, studentId: string) =>
  api.post(`/courses/${courseId}/enroll`, { studentId });

// Lecturers
export const getLecturers = () => api.get('/lecturers');
export const getLecturer = (id: string) => api.get(`/lecturers/${id}`);
export const createLecturer = (data: any) => api.post('/lecturers', data);
export const updateLecturer = (id: string, data: any) => api.put(`/lecturers/${id}`, data);
export const deleteLecturer = (id: string) => api.delete(`/lecturers/${id}`);

// Payments
export const getPayments = (params?: any) => api.get('/payments', { params });
export const getPayment = (id: string) => api.get(`/payments/${id}`);
export const createPayment = (data: any) => api.post('/payments', data);
export const updatePayment = (id: string, data: any) => api.put(`/payments/${id}`, data);

// Leads
export const getLeads = (params?: any) => api.get('/leads', { params });
export const getLead = (id: string) => api.get(`/leads/${id}`);
export const createLead = (data: any) => api.post('/leads', data);
export const updateLead = (id: string, data: any) => api.put(`/leads/${id}`, data);
export const deleteLead = (id: string) => api.delete(`/leads/${id}`);
export const convertLeadToStudent = (id: string) => api.post(`/leads/${id}/convert`);

// Prospects
export const getProspects = (params?: any) => api.get('/prospects', { params });
export const getProspect = (id: string) => api.get(`/prospects/${id}`);
export const createProspect = (data: any) => api.post('/prospects', data);
export const updateProspect = (id: string, data: any) => api.put(`/prospects/${id}`, data);
export const deleteProspect = (id: string) => api.delete(`/prospects/${id}`);
export const convertProspectToStudent = (id: string) => api.post(`/prospects/${id}/convert-to-student`);
export const convertProspectToLead = (id: string) => api.post(`/prospects/${id}/convert-to-lead`);
export const addProspectCommunication = (id: string, data: any) => api.post(`/prospects/${id}/communications`, data);
export const addProspectFollowUp = (id: string, data: any) => api.post(`/prospects/${id}/followups`, data);
export const getProspectDocuments = (prospectId: string) => api.get(`/prospects/${prospectId}/documents`);
export const uploadProspectDocument = (prospectId: string, formData: FormData) =>
  axios.post(`${API_BASE}/prospects/${prospectId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
export const deleteProspectDocument = (prospectId: string, docId: string) =>
  api.delete(`/prospects/${prospectId}/documents/${docId}`);

// Convert lead to prospect
export const convertLeadToProspect = (id: string) => api.post(`/leads/${id}/convert-to-prospect`);

// Communications
export const getCommunications = (params?: any) => api.get('/communications', { params });
export const sendCommunication = (data: any) => api.post('/communications', data);
export const sendGroupCommunication = (data: any) => api.post('/communications/group', data);

// Attendance
export const getAttendance = (params?: any) => api.get('/attendance', { params });
export const createAttendance = (data: any) => api.post('/attendance', data);
export const createBulkAttendance = (records: any[]) => api.post('/attendance/bulk', { records });

// Grades
export const getGrades = (params?: any) => api.get('/grades', { params });
export const createGrade = (data: any) => api.post('/grades', data);
export const updateGrade = (id: string, data: any) => api.put(`/grades/${id}`, data);

// Dashboard
export const getDashboard = () => api.get('/dashboard');
export const getFunnel = () => api.get('/dashboard/funnel');
export const getNeedsAttention = () => api.get('/dashboard/needs-attention');

// Global search
export const globalSearch = (q: string) => api.get('/search', { params: { q } });

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data: any) => api.put('/settings', data);

// Users
export const getUsers = () => api.get('/users');
export const loginUser = (data: any) => api.post('/users/login', data);
export const createUser = (data: any) => api.post('/users', data);
export const updateUser = (id: string, data: any) => api.put(`/users/${id}`, data);
export const deleteUser = (id: string) => api.delete(`/users/${id}`);

// Tasks
export const getTasks = (params?: any) => api.get('/tasks', { params });
export const getTask = (id: string) => api.get(`/tasks/${id}`);
export const createTask = (data: any) => api.post('/tasks', data);
export const updateTask = (id: string, data: any) => api.put(`/tasks/${id}`, data);
export const deleteTask = (id: string) => api.delete(`/tasks/${id}`);

// Events
export const getEvents = () => api.get('/events');
export const createEvent = (data: any) => api.post('/events', data);
export const updateEvent = (id: string, data: any) => api.put(`/events/${id}`, data);
export const deleteEvent = (id: string) => api.delete(`/events/${id}`);

// Reports
export const getStudentsReport = () => api.get('/reports/students');
export const getRevenueReport = () => api.get('/reports/revenue');
export const getAttendanceReport = () => api.get('/reports/attendance');
export const getGradesReport = () => api.get('/reports/grades');
export const getLecturersReport = () => api.get('/reports/lecturers');
export const getLeadsReport = () => api.get('/reports/leads');

// Billing
export const getBillingHistory = () => api.get('/billing');
export const chargeCard = (data: any) => api.post('/billing/charge-card', data);
export const createStandingOrder = (data: any) => api.post('/billing/standing-order', data);
export const createPaymentLink = (data: any) => api.post('/billing/payment-links', data);
export const getPaymentLink = (id: string) => api.get(`/billing/payment-links/${id}`);
export const processPaymentLink = (id: string, data: any) => api.post(`/billing/payment-links/${id}/pay`, data);
export const getAllPaymentLinks = () => api.get('/billing/payment-links-all');

// Signatures
export const getSignatures = (params?: any) => api.get('/signatures', { params });
export const createSignatureRequest = (data: any) => api.post('/signatures', data);
export const getSignatureById = (id: string) => api.get(`/signatures/${id}`);
export const signDocument = (id: string, data: any) => api.post(`/signatures/${id}/sign`, data);

// Accounting
export const getAccountingDocs = (params?: any) => api.get('/accounting', { params });
export const createAccountingDoc = (data: any) => api.post('/accounting', data);
export const getAccountingDoc = (id: string) => api.get(`/accounting/${id}`);
export const getAccountingRevenueReport = () => api.get('/accounting/reports/revenue');
export const getAccountingRevenueAdvanced = (params?: any) => api.get('/accounting/reports/revenue-advanced', { params });
export const getAccountingDocPdf = (id: string) => api.get(`/accounting/${id}/pdf`, { responseType: 'blob' });

// VoicEnter
export const getVoicenterCalls = (params?: any) => api.get('/voicenter/calls', { params });
export const getTodayCalls = () => api.get('/voicenter/calls/today');
export const initiateCall = (data: any) => api.post('/voicenter/calls/initiate', data);
export const scheduleCall = (data: any) => api.post('/voicenter/calls/schedule', data);
export const updateCall = (id: string, data: any) => api.put(`/voicenter/calls/${id}`, data);

// Registrations
export const getRegistrations = () => api.get('/registrations');
export const getRegistration = (id: string) => api.get(`/registrations/${id}`);
export const createRegistration = (data: any) => api.post('/registrations', data);
export const submitRegistration = (id: string, data: any) => api.post(`/registrations/${id}/submit`, data);
export const getRegistrationPdf = (id: string) => api.get(`/registrations/${id}/pdf`);
export const resendRegistration = (id: string) => api.put(`/registrations/${id}/resend`);
export const deleteRegistration = (id: string) => api.delete(`/registrations/${id}`);

export default api;
